import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import api from '@/app/services/axios.service';
import { Spinner } from '@/components/ui/spinner';

import CompleteProfile from './steps/complete-profile';
import ErrorScreen from './steps/error-screen';
// import InviteUsers from './steps/invite-users';
import SignupForm from './steps/signup-form';
// import VerifyOtp from './steps/verify-otp';

// Types
export type OnboardingStep =
  | 'signup'
  // 'verify-otp' | // TODO: add verify-otp step
  | 'complete-profile';
// | 'invite-users';

export interface OnboardingData {
  email?: string;
  name?: string;
  company?: string;
  inviteCode?: string;
  domain?: string;
  ttl?: string;
  role?: 'admin' | 'editor';
}

interface ClientOnboardingResponse {
  id: number;
  invitationCode: string;
  clientDomain: string;
  userEmail: string;
  ttl: string;
  clientName: string;
  createdAt: string;
  updatedAt: string;
  role?: 'admin' | 'editor';
}

export default function Onboarding() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();

  // State for onboarding data and current step
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    company: '', // Will be updated when invite code is validated
    domain: '',
    email: '',
    inviteCode: '',
    ttl: '',
  });
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('signup');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateInviteCode = async () => {
      try {
        if (!inviteCode) {
          setError('Invalid invitation code');
          setIsLoading(false);
          return;
        }

        const response = await api.get(`/client-onboarding/${inviteCode}`);

        if (response.status < 200 || response.status >= 300) {
          if (response.status === 404) {
            setError('Invalid invitation code');
          } else {
            setError('Failed to validate invitation code');
          }
          setIsLoading(false);
          return;
        }

        const data: ClientOnboardingResponse = response.data;

        // Check if the invitation has expired
        const ttlDate = new Date(data.ttl);
        if (ttlDate < new Date()) {
          setError('This invitation has expired');
          setIsLoading(false);
          return;
        }

        setOnboardingData(prev => ({
          ...prev,
          company: data.clientName,
          domain: data.clientDomain,
          email: data.userEmail,
          inviteCode: data.invitationCode,
          ttl: data.ttl,
          role: data.role,
        }));
      } catch (err) {
        setError('Failed to validate invitation code');
      } finally {
        setIsLoading(false);
      }
    };

    validateInviteCode();
  }, [inviteCode]);

  // Handle navigation between steps
  const handleNext = (stepData: Partial<OnboardingData> = {}, nextStep?: OnboardingStep) => {
    // Update onboarding data with new data from the current step
    const updatedData = { ...onboardingData, ...stepData };
    setOnboardingData(updatedData);

    // Determine the next step
    if (nextStep) {
      setCurrentStep(nextStep);
    } else {
      // Default progression
      switch (currentStep) {
        case 'signup':
          setCurrentStep('complete-profile');
          break;
        case 'complete-profile':
          // Only go to invite-users step if role is admin, otherwise go directly to home
          // if (updatedData.role?.toLowerCase() === 'admin') {
          //   setCurrentStep('invite-users');
          // } else {
          //   navigate('/');
          // }
          navigate('/');
          break;
        // case 'invite-users':
        //   navigate('/');
        //   break;
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col w-full min-h-screen bg-background text-foreground font-[Inter]">
        <img
          src="/auth-background.svg"
          alt="auth-bg"
          className="fixed top-0 left-0 object-cover w-full h-full pointer-events-none"
        />
        <div className="relative z-10 flex items-center justify-center flex-1 p-4">
          <div className="text-center">
            <Spinner className="mx-auto mb-4" size={32} />
            <p className="text-muted-foreground">Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // If there's an error, show error screen
  if (error) {
    return <ErrorScreen message={error || 'Unknown error'} />;
  }

  // Get the current step component
  const renderStep = () => {
    switch (currentStep) {
      case 'signup':
        return <SignupForm onNext={handleNext} data={onboardingData} />;
      // case 'verify-otp':
      //   return <VerifyOtp onNext={handleNext} data={onboardingData} />;
      case 'complete-profile':
        return <CompleteProfile onNext={handleNext} data={onboardingData} />;
      // case 'invite-users':
      //   // Extra safety check - only show invite-users if role is admin
      //   if (onboardingData.role?.toLowerCase() === 'admin') {
      //     return <InviteUsers onNext={handleNext} data={onboardingData} />;
      //   } else {
      //     // Redirect to home if somehow ended up here without admin role
      //     navigate('/');
      //     return null;
      //   }
      default:
        return <ErrorScreen message="Unknown step" />;
    }
  };

  // Render the step within the wrapper
  return (
    <div className="flex flex-col w-full min-h-screen bg-background text-foreground font-[Inter]">
      <img
        src="/auth-background.svg"
        alt="auth-bg"
        className="fixed top-0 left-0 object-cover w-full h-full pointer-events-none"
      />
      {/* Main content */}
      <main className="relative z-10 flex items-center justify-center flex-1 p-4">
        {renderStep()}
      </main>
    </div>
  );
}
